import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Divider,
  Avatar,
} from '@mui/material';
import {
  ArrowBack,
  Delete,
  EventNote,
  Assignment,
  Description,
  EmojiEvents,
  SportsBasketball,
  MusicNote,
  Groups,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useToolkit } from '../contexts/ToolkitContext';

const ToolkitPage = () => {
  const navigate = useNavigate();
  const { toolkit, removeTool, getTotalTools } = useToolkit();

  const personaIcons = {
    sports: <SportsBasketball />,
    music: <MusicNote />,
    clubs: <Groups />,
  };

  const personaColors = {
    sports: '#89479a',
    music: '#f78f29',
    clubs: '#b14668',
  };

  const toolIcons = {
    'Practice Planner': <EventNote />,
    'Tournament Manager': <EmojiEvents />,
    'Music Library': <Description />,
    'Meeting Scheduler': <Assignment />,
    'Attendance Tracker': <Assignment />,
  };

  const getPersonaDisplayName = (persona) => {
    return persona === 'sports' ? 'Sports' : persona === 'music' ? 'Music & Arts' : 'Clubs';
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fafafa', pt: 8 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/')} sx={{ color: 'primary.main' }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" color="primary">
            My Toolkit
          </Typography>
          <Chip 
            label={`${getTotalTools()} tools`} 
            color="primary" 
            variant="outlined"
            sx={{ ml: 'auto' }}
          />
        </Box>

        {/* Toolkit Overview */}
        <Paper sx={{ p: 3, mb: 4, backgroundColor: '#ffffff', border: '2px solid #89479a', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Your Personalized Toolkit
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your selected tools for each activity area. These tools have been customized based on your preferences.
          </Typography>
        </Paper>

        {/* All Tools */}
        <Paper sx={{ p: 3, mb: 4, backgroundColor: '#ffffff', border: '2px solid #89479a', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Your Tools
          </Typography>
          
          {getTotalTools() > 0 ? (
            <List>
              {Array.from(new Set(Object.values(toolkit).flat())).map((tool, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                    {toolIcons[tool] || <EventNote />}
                    <ListItemText 
                      primary={tool}
                      primaryTypographyProps={{ variant: 'body1' }}
                    />
                  </Box>
                  <ListItemSecondaryAction>
                    <IconButton 
                      edge="end" 
                      size="small"
                      onClick={() => {
                        // Remove tool from all personas
                        Object.keys(toolkit).forEach(persona => {
                          if (toolkit[persona].includes(tool)) {
                            removeTool(persona, tool);
                          }
                        });
                      }}
                      sx={{ color: '#89479a' }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                You haven't added any tools to your kit yet.
              </Typography>
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => navigate('/')}
              >
                Add Tools
              </Button>
            </Box>
          )}
        </Paper>

        {/* Action Buttons */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button 
            variant="contained" 
            color="primary" 
            sx={{ mr: 2, px: 3 }}
            onClick={() => navigate('/')}
          >
            Add More Tools
          </Button>
          <Button 
            variant="outlined" 
            sx={{ borderColor: '#89479a', color: '#89479a' }}
          >
            Export Toolkit
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ToolkitPage;
